import { useState, useEffect } from 'react';
import ScrollPage from '@/components/ScrollPage';
import useAxios from '@/hooks/useAxios';
import { config } from '@/config';
import { toast } from '@/components/Toast';
import { FileUpload } from '@/components/FileUpload';
import { User, Mail, Phone, Badge, CreditCard, Building2, Save, Loader2, FileText, ExternalLink, Download, Upload, Trash2 } from 'lucide-react';
import { UserProfile } from '../users/types';
import PaymentInstruments from '@/components/payment-instruments';



export default function Profile() {
    const axios = useAxios();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [profileData, setProfileData] = useState<UserProfile>({
        email: '',
        name: '',
        phone: '',
        customer: null,
        role: '',
        ssnCode: null,
        bankAccountNumber: null,
        bankRoutingNumber: null,
        bankAccountName: null,
    });
    const [selectedImages, setSelectedImages] = useState<File[]>([]);
    const [uploadingFile, setUploadingFile] = useState(false);
    const [downloadingFileId, setDownloadingFileId] = useState<string | null>(null);

    useEffect(() => {
        fetchProfile();
    }, []);

    const fetchProfile = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${config.backendUrl}/users/profile`);
            setProfileData(response.data);
        } catch (error: any) {
            console.error('Failed to fetch profile:', error);
            toast.error(error.response?.data?.message || 'Failed to load profile');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (field: keyof UserProfile, value: string | null) => {
        setProfileData(prev => ({
            ...prev,
            [field]: value
        }));
    };


    const handleSave = async () => {
        if (!profileData.name || !profileData.name.trim()) {
            toast.error('Name is required');
            return;
        }
        try {
            setSaving(true);
            const payload = {
                name: profileData.name.trim(),
                phone: profileData.phone,
                ssnCode: profileData.ssnCode ?? null,
            };
            await axios.put(`${config.backendUrl}/users/profile`, payload);
            fetchProfile();
            toast.success('Profile updated successfully');
        } catch (error: any) {
            console.error('Failed to update profile:', error);
            toast.error(error.response?.data?.message || 'Failed to update profile');
        } finally {
            setSaving(false);
        }
    };


    const filesBase = `${config.backendUrl}${config.filesBase}`;
    const IMAGE_ACCEPT = '*';
    const IMAGE_MAX_SIZE = 10 * 1024 * 1024; // 10 MB per image

    const handleUploadFile = async () => {
        if (selectedImages.length === 0) return;
        try {
            setUploadingFile(true);
            const formData = new FormData();
            formData.append('from', 'profile');
            selectedImages.forEach(file => formData.append('file', file));
            const res = await axios.post(`${filesBase}/batch`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            const files = res.data?.files ?? [];
            setSelectedImages([]);
            toast.success(files.length === 1 ? 'File uploaded' : `${files.length} files uploaded`);
            await fetchProfile();
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Failed to upload files');
        } finally {
            setUploadingFile(false);
        }
    };
    const handleViewFile = async (id: string) => {
        const w = window.open('', '_blank');
        if (!w) {
            toast.error('Popup blocked. Please allow popups and try again.');
            return;
        }
        try {
            w.document.write('<html><body style="margin:0;display:flex;align-items:center;justify-content:center;min-height:100vh;">Loading...</body></html>');
            const res = await axios.get(`${filesBase}/${id}/file`, { responseType: 'blob' });
            const url = URL.createObjectURL(res.data);
            w.location.replace(url);
            setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch (error: any) {
            w.close();
            toast.error(error.response?.data?.message || 'Failed to load file');
        }
    };
    const handleDownloadFile = async (id: string, fileName: string) => {
        try {
            setDownloadingFileId(id);
            const res = await axios.get(`${filesBase}/${id}/file?download=1`, { responseType: 'blob' });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName || 'file';
            a.click();
            URL.revokeObjectURL(url);
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Failed to download file');
        } finally {
            setDownloadingFileId(null);
        }
    };
    const handleDeleteFile = async (id: string) => {
        try {
            await axios.del(`${filesBase}/${id}`);
            setProfileData(prev => ({
                ...prev,
                files: (prev.files ?? []).filter(f => f.id !== id),
            }));
            toast.success('File deleted');
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Failed to delete file');
        }
    };

    if (loading) {
        return (
            <ScrollPage>
                <div className="flex items-center justify-center h-64">
                    <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                </div>
            </ScrollPage>
        );
    }

    return (
        <ScrollPage>
            {/* Personal Information */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-6">Personal Information</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            <User className="h-4 w-4 inline mr-1" />
                            Name <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={profileData.name}
                            onChange={(e) => handleChange('name', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Enter your name"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            <Mail className="h-4 w-4 inline mr-1" />
                            Email
                        </label>
                        <input
                            type="email"
                            value={profileData.email}
                            disabled
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            <Phone className="h-4 w-4 inline mr-1" />
                            Phone
                        </label>
                        <input
                            type="tel"
                            value={profileData.phone}
                            onChange={(e) => handleChange('phone', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Enter your phone number"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            <Badge className="h-4 w-4 inline mr-1" />
                            SSN Code
                        </label>
                        <input
                            type="text"
                            value={profileData.ssnCode || ''}
                            onChange={(e) => handleChange('ssnCode', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Enter your SSN code"
                        />
                    </div>
                </div>
                <div className="mt-6">
                    <button
                        type="button"
                        onClick={handleSave}
                        disabled={saving}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                        Save Changes
                    </button>
                </div>


            </div>

            {/* Bank Account Information */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mt-6">
                <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <CreditCard className="h-4 w-4" />
                    Bank Account Information
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                    Used for reward payouts. Your information is stored securely.
                </p>
                <div className="flex flex-wrap">
                    <div className="pt-2">
                        <PaymentInstruments />
                    </div>
                </div>
            </div>

            {/* Standalone file upload – multiple images */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mt-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <Upload className="h-5 w-5" />
                    File upload
                </h2>
                <p className="text-sm text-gray-500 mb-4">
                    Select multiple Files (max 10MB each, up to 20 at a time). Then upload download, or delete below.
                </p>
                <div className="flex flex-col gap-4">
                    <FileUpload
                        label="Choose files"
                        hint="Click or drag and drop; max 10MB each, up to 20."
                        accept={IMAGE_ACCEPT}
                        maxSizeBytes={IMAGE_MAX_SIZE}
                        multiple
                        value={selectedImages}
                        onChange={setSelectedImages}
                    />
                    <button
                        type="button"
                        onClick={handleUploadFile}
                        disabled={selectedImages.length === 0 || uploadingFile}
                        className="self-start px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                        {uploadingFile ? (
                            <>
                                <Loader2 className="h-4 w-4 animate-spin" />
                                Uploading...
                            </>
                        ) : (
                            <>
                                <Upload className="h-4 w-4" />
                                Upload {selectedImages.length > 0 ? `${selectedImages.length} file(s)` : 'files'}
                            </>
                        )}
                    </button>
                    {(profileData.files?.length ?? 0) > 0 && (
                        <div className="mt-2">
                            <p className="text-sm font-medium text-gray-700 mb-2">Your files:</p>
                            <ul className="space-y-2">
                                {(profileData.files ?? []).map((f) => (
                                    <li key={f.id} className="flex items-center gap-3 text-sm">
                                        <FileText className="h-4 w-4 text-gray-500 shrink-0" />
                                        <span className="truncate text-gray-700">{f.fileName}</span>
                                        <span className="flex items-center gap-1 shrink-0">

                                            <button
                                                type="button"
                                                onClick={() => handleDownloadFile(f.id, f.fileName)}
                                                disabled={downloadingFileId === f.id}
                                                className="text-blue-600 hover:text-blue-800 hover:underline disabled:opacity-60 disabled:no-underline"
                                            >
                                                {downloadingFileId === f.id ? (
                                                    <span className="inline-flex items-center gap-1">
                                                        <Loader2 className="h-3 w-3 animate-spin" />
                                                        Downloading...
                                                    </span>
                                                ) : (
                                                    "Download"
                                                )}
                                            </button>
                                            <span className="text-gray-300">|</span>
                                            <button
                                                type="button"
                                                onClick={() => handleDeleteFile(f.id)}
                                                className="text-red-600 hover:text-red-800 hover:underline flex items-center gap-0.5"
                                            >
                                                <Trash2 className="h-3.5 w-3.5" />
                                                Delete
                                            </button>
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            </div>


            {profileData.customer && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mt-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-6">Customer Information</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                <Building2 className="h-4 w-4 inline mr-1" />
                                Customer Name
                            </label>
                            <input
                                type="text"
                                value={profileData.customer.name || ''}
                                disabled
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                <Badge className="h-4 w-4 inline mr-1" />
                                Customer Code
                            </label>
                            <input
                                type="text"
                                value={profileData.customer.cardCode || ''}
                                disabled
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                            />
                        </div>
                    </div>
                </div>
            )}
        </ScrollPage>
    );
}
